import React, { createContext, useContext, useState, ReactNode } from 'react';

interface DurationOption {
  title: string;
  isActive: boolean;
  val: string;
}

interface ResultListContextProps {
  activeFilters: { [key: string]: boolean };
  setActiveFilters: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  workspaceType: Record<string, boolean>;
  setWorkspaceType: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  currentDuration: string;
  setCurrentDuration: React.Dispatch<React.SetStateAction<string>>;
  currentLocation: { rad: number; lat: number; lng: number };
  setCurrentLocation: React.Dispatch<React.SetStateAction<{ rad: number; lat: number; lng: number }>>;
  // probably useless state below
  currentResources: any;
  setCurrentResources: React.Dispatch<React.SetStateAction<any>>;
  // probably useless state above
  durationOptions: DurationOption[];
  setDurationOptions: React.Dispatch<React.SetStateAction<DurationOption[]>>;
  spacesCount: any;
  setSpacesCount: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  currentAmenities: string;
  setCurrentAmenities: React.Dispatch<React.SetStateAction<string>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  budgetRange: number[];
  setBudgetRange: React.Dispatch<React.SetStateAction<number[]>>;
  budgetValues: number[];
  setBudgetValues: React.Dispatch<React.SetStateAction<number[]>>;
  budgetValuesPrev: number[];
  setBudgetValuesPrev: React.Dispatch<React.SetStateAction<number[]>>;
  histogramValues: number[];
  setHistogramValues: React.Dispatch<React.SetStateAction<number[]>>;
  amenitiesPayload: string;
  setAmenitiesPayload: React.Dispatch<React.SetStateAction<string>>;
  breadcrumbs: { [key: string]: string }[]
  setBreadcrumbs: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  firstLoad: boolean;
  setFirstLoad: React.Dispatch<React.SetStateAction<boolean>>;
  onlyWorkspaceSelected: boolean;
  setOnlyWorkspaceSelected: React.Dispatch<React.SetStateAction<boolean>>;
  availableAmenities: Record<string, boolean>;
  setAvailableAmenities: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  officeTypes: {
    key: number;
    text: string;
    value: string;
    singleVersionTitle?: string;
    singleVersionValue?: string;
    imageURL: string;
    description: string;
  }[],
}

const ResultListContext = createContext<ResultListContextProps | undefined>(undefined);

interface ResultListProviderProps {
  children: ReactNode;
}

export const ResultListProvider: React.FC<ResultListProviderProps> = ({ children }) => {
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: boolean }>({});
  const [workspaceType, setWorkspaceType] = useState<Record<string, boolean>>({});
  const [currentDuration, setCurrentDuration] = useState('');
  const [currentLocation, setCurrentLocation] = useState({ rad: 100, lat: 0, lng: 0 });
  const [currentResources, setCurrentResources] = useState<any>([]);
  const [durationOptions, setDurationOptions] = useState<DurationOption[]>([
    { title: 'Daily', isActive: false, val: 'day' },
    { title: 'Monthly', isActive: false, val: 'month' },
    { title: 'Annually', isActive: false, val: 'year' },
  ]);
  const [spacesCount, setSpacesCount] = useState<Record<string, number>>({
    numSpaces: 0,
    numSpacesFiltered: 0,
    deskCount: 0,
    privateOfficeCount: 0,
    virtualOfficeCount: 0,
    loungeCount: 0,
  });
  const [currentAmenities, setCurrentAmenities] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState<string>('');
  const [budgetRange, setBudgetRange] = useState<number[]>([]);
  const [budgetValues, setBudgetValues] = useState<number[]>([]);
  const [budgetValuesPrev, setBudgetValuesPrev] = useState<number[]>([]);
  const [histogramValues, setHistogramValues] = useState([]);
  const [amenitiesPayload, setAmenitiesPayload] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<any>([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [onlyWorkspaceSelected, setOnlyWorkspaceSelected] = useState(false);
  const [availableAmenities, setAvailableAmenities] = useState({});

  const officeTypes = [
    {
        "key": 4,
        "text": "Coworking Desks",
        "value": "hot_desk",
        "singleVersionTitle": "Coworking Desk",
        "singleVersionValue": "hot_desk",
        "imageURL": "/cloudfront/react-dist/static/media/desks.7cec66d3b87c186d5f0b.jpg",
        "description": "To find your ideal office, our flexible office advisory team undertake extensive searches with a network of trusted office operators. A Coworker advisor will be in touch to discuss the best options for you."
    },
    {
        "key": 1,
        "text": "Private office",
        "value": "private_office",
        "imageURL": "/cloudfront/react-dist/static/media/private-office.9a4121155f8d2fc78c53.jpg",
        "description": "To find your ideal office, our flexible office advisory team undertake extensive searches with a network of trusted office operators. A Coworker advisor will be in touch to discuss the best options for you."
    },
    {
        "key": 6,
        "text": "Virtual office",
        "value": "virtual_office",
        "imageURL": "/cloudfront/react-dist/static/media/virtual-office.6b10270d2648c35ba9e6.jpeg",
        "description": "A virtual office provides a physical address and office-related services such as a mailing address, phone answering services, meeting rooms, and videoconferencing. The physical office is not assigned and workspace may be used on an ad-hoc basis."
    },
  ]

  return (
    <ResultListContext.Provider
      value={{
        activeFilters,
        setActiveFilters,
        workspaceType,
        setWorkspaceType,
        currentDuration,
        setCurrentDuration,
        currentLocation,
        setCurrentLocation,
        currentResources,
        setCurrentResources,
        durationOptions,
        setDurationOptions,
        spacesCount,
        setSpacesCount,
        currentAmenities,
        setCurrentAmenities,
        currentPage,
        setCurrentPage,
        sortBy,
        setSortBy,
        budgetRange,
        setBudgetRange,
        budgetValues,
        setBudgetValues,
        budgetValuesPrev,
        setBudgetValuesPrev,
        histogramValues,
        setHistogramValues,
        amenitiesPayload,
        setAmenitiesPayload,
        breadcrumbs,
        setBreadcrumbs,
        firstLoad, 
        setFirstLoad,
        onlyWorkspaceSelected,
        setOnlyWorkspaceSelected,
        availableAmenities,
        setAvailableAmenities,
        officeTypes,
      }}
    >
      {children}
    </ResultListContext.Provider>
  );
};

export const useResultListContext = () => {
  const context = useContext(ResultListContext);
  if (context === undefined) {
    throw new Error('useResultListContext must be used within an ResultListProvider');
  }
  return context;
};
